import { CheckOutlined, DownOutlined, LoadingOutlined } from "@ant-design/icons";
import { Button, Dropdown, Space, Tooltip } from "antd";
import type React from "react";
import { useEffect, useRef, useState } from "react";
import styles from "../../pages/notes/notes.module.scss";
import { getEHRTransferStatus } from "../../service/notes.service";
import type { EHRTransferStatus, TransferJob, TransferTargetOption } from "../../types/index.type";
import { EventType, trackEvent } from "../../utils/analytics";
import { useOptionalTransfer } from "./TransferContext";
import TransferTargetMenu from "./TransferTargetMenu";
import {
  type ButtonView,
  type TargetEntry,
  buttonView,
  isSplitEnabled,
  pickRetryTarget,
  suggestedOption,
  summaryLabel,
} from "./transferTarget";

const TERMINAL_STATUSES = new Set<EHRTransferStatus>(["completed", "failed", "cancelled"]);

interface TransferToEHRButtonProps {
  // The note this button initiates a transfer for. Required so the global
  // TransferContext knows which audio to transfer, and so the button can tell
  // whether the user's active transfer is for THIS note vs. a different one.
  audioId: number;
  className?: string;
  icon?: React.ReactNode;
}

const ACTIVE_STATUSES = ["pending", "logging_in", "awaiting_mfa", "patient_lookup", "template_mapping", "writing_note"];
const AWAITING_SELECTION_STATUSES = new Set(["awaiting_appointment", "awaiting_note_type"]);

// Quiet line under the split button (readiness whisper / no-default reason).
const WHISPER_STYLE: React.CSSProperties = { fontSize: 11, color: "#6b7280", marginTop: 4, textAlign: "right" };
const SCHEDULE_UPDATED_MS = 3000;

const TransferToEHRButton: React.FC<TransferToEHRButtonProps> = ({ audioId, className = "", icon: defaultIcon }) => {
  // useOptionalTransfer (not useTransfer) because this header is reused on
  // `/notesforMobile`, which is routed outside AuthLayout and therefore has
  // no TransferProvider. Throwing there crashed the whole subtree.
  const transfer = useOptionalTransfer();
  // This note's own live job (independent of other notes' transfers, which can
  // now run concurrently). Recomputed from the context's job list each render.
  const thisNoteJob = transfer?.getJobForAudio(audioId) ?? null;

  // Per-note historical status. The global TransferContext only knows about
  // *active* (or just-finished) jobs, so a note transferred yesterday looks
  // like "not_started" to it. We fetch this note's most recent job on mount
  // so a previously-transferred note correctly renders "Transferred".
  const [historicalJob, setHistoricalJob] = useState<TransferJob | null>(null);

  useEffect(() => {
    let cancelled = false;
    getEHRTransferStatus(audioId)
      .then((res) => {
        if (cancelled) return;
        setHistoricalJob(res.data?.job ?? null);
      })
      .catch((err) => console.error("Failed to fetch transfer history for note:", err));
    return () => {
      cancelled = true;
    };
  }, [audioId]);

  // The one-click target: read once per note (mounts share the provider's
  // entry). A cache read only — it never starts an EHR login. Released on
  // unmount so only notes on screen are re-read later.
  const ensureTarget = transfer?.ensureTarget;
  const releaseTarget = transfer?.releaseTarget;
  useEffect(() => {
    ensureTarget?.(audioId);
    return () => releaseTarget?.(audioId);
  }, [ensureTarget, releaseTarget, audioId]);

  // When this note's live job reaches a terminal state, persist it as the
  // historical state so the button stays correct even after the active
  // endpoint stops returning the job. A ref guard makes the per-poll effect
  // idempotent (the live job object is a fresh reference on every poll).
  const lastPersistedRef = useRef<string | null>(null);
  useEffect(() => {
    if (!thisNoteJob || !TERMINAL_STATUSES.has(thisNoteJob.status)) return;
    const key = `${thisNoteJob.id}:${thisNoteJob.status}`;
    if (lastPersistedRef.current === key) return;
    lastPersistedRef.current = key;
    setHistoricalJob(thisNoteJob);
  }, [thisNoteJob]);

  // Caret menu open state. While open, the entry the menu renders is frozen
  // so a background refresh can never reorder rows under the cursor; the
  // label only changes once the menu closes.
  const [menuOpen, setMenuOpen] = useState(false);
  const frozenEntryRef = useRef<TargetEntry | null>(null);
  // Set by an explicit refresh inside the open menu: show live state until the
  // fresh rows land, then freeze again so nothing reorders under the cursor.
  const refreezeRef = useRef(false);

  // "Schedule updated": the suggestion moved after it was first shown (tab
  // return, a transfer on another note of this patient). Named for a few
  // seconds so the next click is an informed one. The frozen entry means it
  // can only change while the menu is closed.
  const visibleEntry =
    menuOpen && frozenEntryRef.current ? frozenEntryRef.current : transfer?.getTargetForAudio(audioId);
  const visibleSuggestionId = suggestedOption(visibleEntry?.target)?.id ?? null;
  const lastSuggestionRef = useRef<string | null>(null);
  const [scheduleUpdated, setScheduleUpdated] = useState(false);
  useEffect(() => {
    const previous = lastSuggestionRef.current;
    if (visibleSuggestionId) lastSuggestionRef.current = visibleSuggestionId;
    if (!visibleSuggestionId || !previous || previous === visibleSuggestionId) return;
    setScheduleUpdated(true);
    const timer = setTimeout(() => setScheduleUpdated(false), SCHEDULE_UPDATED_MS);
    return () => {
      clearTimeout(timer);
      setScheduleUpdated(false);
    };
  }, [visibleSuggestionId]);

  // Analytics: once per distinct target block shown (ids and counts only).
  const shownTarget = visibleEntry?.target;
  const shownKey =
    shownTarget?.enabled && shownTarget.state !== "not_applicable"
      ? `${shownTarget.state}:${shownTarget.suggested_id ?? ""}:${shownTarget.no_default_reason ?? ""}`
      : "";
  const shownOptions = shownTarget?.options.length ?? 0;
  const shownDistance = suggestedOption(shownTarget)?.distance_days ?? null;
  const shownState = shownTarget?.state;
  const shownReason = shownTarget?.no_default_reason ?? null;
  useEffect(() => {
    if (!shownKey) return;
    trackEvent(EventType.TRANSFER_TARGET_SHOWN, {
      state: shownState,
      options: shownOptions,
      no_default_reason: shownReason,
      suggested_distance_days: shownDistance,
    });
  }, [shownKey, shownState, shownOptions, shownReason, shownDistance]);

  if (!transfer) return null;
  const { isLoading, credentialStatus, startTransfer, getTargetForAudio, refreshTarget, getLastRequestedTarget } =
    transfer;

  // Effective status priority: live job for this note > historical > idle.
  const effectiveJob = thisNoteJob ?? historicalJob;
  const effectiveStatus: EHRTransferStatus =
    (thisNoteJob?.status as EHRTransferStatus | undefined) ??
    (historicalJob?.status as EHRTransferStatus | undefined) ??
    "not_started";

  const isInProgress = ACTIVE_STATUSES.includes(effectiveStatus);
  const isAwaitingSelection = AWAITING_SELECTION_STATUSES.has(effectiveStatus);
  const isAwaitingMFA = effectiveStatus === "awaiting_mfa";
  const isCompleted = effectiveStatus === "completed";
  // Cancelled is retryable just like failed.
  const isRetryable = effectiveStatus === "failed" || effectiveStatus === "cancelled";
  const isNoteTypeSelection = effectiveStatus === "awaiting_note_type";
  const hasCredentials = credentialStatus?.has_credential ?? false;
  // NOTE: no cross-note "another transfer running" block — concurrent
  // transfers across different notes are supported.
  const isDisabled = isLoading || isInProgress || isCompleted || isAwaitingSelection || isAwaitingMFA;

  const liveEntry = getTargetForAudio(audioId);
  if (
    menuOpen &&
    refreezeRef.current &&
    liveEntry.settled &&
    !liveEntry.fetching &&
    !liveEntry.target?.refreshing &&
    liveEntry.target?.state !== "loading"
  ) {
    frozenEntryRef.current = liveEntry;
    refreezeRef.current = false;
  }
  const entry = menuOpen && frozenEntryRef.current ? frozenEntryRef.current : liveEntry;

  // ---- Split button (one-click) -------------------------------------------
  // Only for a note that can be transferred right now (never started, or a
  // failed/cancelled attempt to retry). In flight, awaiting a choice, and
  // transferred all keep today's button exactly.
  const canStart = hasCredentials && !isLoading && (effectiveStatus === "not_started" || isRetryable);
  if (canStart && isSplitEnabled(liveEntry)) {
    const view: ButtonView = buttonView({
      entry,
      retrying: isRetryable,
      lastRequestedId: getLastRequestedTarget(audioId),
    });
    const target = entry.target;
    const suggestion = suggestedOption(target);

    const start = (appointmentId: string | null, source: "suggested" | "menu" | "retry" | "legacy") => {
      trackEvent(EventType.TRANSFER_ONE_CLICK, { source, has_target: !!appointmentId });
      return startTransfer(audioId, appointmentId);
    };

    const handlePrimary = () => {
      if (isRetryable) {
        const retryId = pickRetryTarget(target, getLastRequestedTarget(audioId));
        void start(retryId, retryId ? "retry" : "legacy");
        return;
      }
      if (view.kind === "suggest" && suggestion) {
        void start(suggestion.id, "suggested");
        return;
      }
      if (view.kind === "choose") {
        openMenu();
        return;
      }
      // loading / empty / unavailable / no credential: today's path exactly.
      void start(null, "legacy");
    };

    const openMenu = () => {
      frozenEntryRef.current = liveEntry;
      setMenuOpen(true);
    };
    const onOpenChange = (open: boolean) => {
      if (open) openMenu();
      else {
        setMenuOpen(false);
        frozenEntryRef.current = null;
        refreezeRef.current = false;
      }
    };

    const handlePick = (option: TransferTargetOption) => {
      setMenuOpen(false);
      frozenEntryRef.current = null;
      void start(option.id, "menu");
    };

    const subNode =
      view.kind === "loading" ? (
        <span
          role="status"
          aria-label="Loading the schedule"
          style={{
            display: "inline-block",
            width: 88,
            height: 10,
            borderRadius: 5,
            background: "rgba(255,255,255,.45)",
            verticalAlign: "middle",
          }}
        />
      ) : view.sub ? (
        <span style={{ opacity: 0.85, fontWeight: 400 }}>{view.sub}</span>
      ) : null;

    const tooltip =
      suggestion && view.kind === "suggest"
        ? [suggestion.visit_type, suggestion.provider, suggestion.location || suggestion.department, suggestion.date]
            .filter(Boolean)
            .join(" · ")
        : undefined;

    const caretDisabled = !view.caretEnabled;
    const whisper = scheduleUpdated && view.kind === "suggest" && !isRetryable ? "Schedule updated" : view.whisper;

    return (
      <div style={{ display: "inline-flex", flexDirection: "column", alignItems: "flex-end" }}>
        <Space.Compact className={`${className} ${styles["notes__transfer-split"]}`} style={{ display: "inline-flex" }}>
          <Tooltip title={tooltip} mouseEnterDelay={0.4}>
            <Button
              type="primary"
              onClick={handlePrimary}
              icon={defaultIcon}
              aria-label={view.sub ? `${view.text} ${view.sub}` : view.text}
              style={{ display: "flex", alignItems: "center", gap: 6 }}
            >
              <span>{view.text}</span>
              {subNode}
            </Button>
          </Tooltip>
          <Dropdown
            open={menuOpen}
            onOpenChange={onOpenChange}
            trigger={["click"]}
            placement="bottomRight"
            disabled={caretDisabled}
            dropdownRender={() => (
              <TransferTargetMenu
                entry={entry}
                onPick={handlePick}
                onRefresh={() => {
                  // An explicit refresh shows live state (loading, then the
                  // fresh rows) instead of the snapshot frozen when the menu
                  // opened.
                  frozenEntryRef.current = null;
                  refreezeRef.current = true;
                  refreshTarget(audioId, true);
                }}
              />
            )}
          >
            <Button
              type="primary"
              disabled={caretDisabled}
              aria-label="Choose a different appointment"
              aria-haspopup="menu"
              aria-expanded={menuOpen}
              style={{ display: "flex", alignItems: "center", gap: 4 }}
            >
              {view.badge != null && (
                <span
                  style={{
                    background: "rgba(255,255,255,.25)",
                    borderRadius: 9,
                    padding: "0 6px",
                    fontSize: 11,
                    lineHeight: "16px",
                  }}
                >
                  {view.badge}
                </span>
              )}
              <DownOutlined style={{ fontSize: 11 }} />
            </Button>
          </Dropdown>
        </Space.Compact>
        {whisper && (
          <span aria-live="polite" style={WHISPER_STYLE}>
            {whisper}
          </span>
        )}
      </div>
    );
  }

  // ---- Today's button (not applicable, endpoint missing, fetch failed) -----
  let buttonText = "Transfer to EHR";
  let icon: React.ReactNode = null;

  const isProcessingState = isLoading || isInProgress || isAwaitingSelection || isAwaitingMFA;
  const style = { color: "white" };
  if (isLoading) {
    buttonText = "Checking...";
    icon = <LoadingOutlined style={style} />;
  } else if (isCompleted) {
    buttonText = "Transferred";
    icon = <CheckOutlined style={style} />;
  } else if (isRetryable) {
    buttonText = "Retry Transfer";
  } else if (!hasCredentials) {
    buttonText = "Transfer to EHR";
  } else if (isInProgress) {
    buttonText = "Transferring...";
    icon = <LoadingOutlined style={style} />;
  } else if (isAwaitingMFA) {
    buttonText = "Enter MFA Code...";
    icon = <LoadingOutlined style={style} />;
  } else if (isAwaitingSelection) {
    buttonText = isNoteTypeSelection ? "Select Note Template..." : "Select Appointment...";
    icon = <LoadingOutlined style={style} />;
  } else if (hasCredentials) {
    buttonText = "Transfer to EHR";
  }
  // Name where the note is going / went, when the job knows (one-click or not).
  const where = isInProgress || isCompleted ? summaryLabel(effectiveJob?.selected_appointment_summary) : "";
  if (where) buttonText = `${buttonText} · ${where}`;

  return (
    <Button
      type="primary"
      className={className}
      style={{
        display: "flex",
        alignItems: "center",
        ...((isProcessingState || isCompleted) && {
          backgroundColor: "#1677ff",
          borderColor: "#1677ff",
          color: "white",
          opacity: 1,
        }),
      }}
      onClick={() => startTransfer(audioId)}
      disabled={isDisabled}
      icon={icon ?? defaultIcon}
    >
      {buttonText}
    </Button>
  );
};

export default TransferToEHRButton;
